import React, { useCallback, useEffect, useState } from 'react';
import { FileText, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';
import { SignaturePad } from '../common/SignaturePad';
import {
  TELEMED_AGREEMENT_VERSION,
  TELEMED_AGREEMENT_TITLE,
  TELEMED_AGREEMENT_SECTIONS
} from '../../content/telemedAgreement';

export interface AgreementStatus {
  applicable: boolean;
  agreementType: string | null;
  agreementAccepted: boolean;
  agreementVersion: string | null;
  agreementAcceptedAt: string | null;
  /** False for an acceptance recorded by an admin rather than actually signed. */
  hasSignature: boolean;
}

/**
 * Derive what the clinic still needs to do. Kept as a standalone export so the
 * dashboard banner and this card can never disagree about who is prompted.
 */
export function agreementNeedsSignature(s?: AgreementStatus | null): boolean {
  if (!s?.applicable) return false;
  return !s.agreementAccepted || !s.hasSignature;
}

export function agreementIsOutdated(s?: AgreementStatus | null): boolean {
  if (!s?.applicable || !s.agreementAccepted || !s.hasSignature) return false;
  return !!s.agreementVersion && s.agreementVersion !== TELEMED_AGREEMENT_VERSION;
}

const formatDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

interface Props {
  physicianId: string;
  onSigned?: () => void;
}

export const AgreementCard: React.FC<Props> = ({ physicianId, onSigned }) => {
  const [status, setStatus] = useState<AgreementStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showText, setShowText] = useState(false);
  const [signing, setSigning] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.getPhysicianAgreement(physicianId);
    if (res.success) setStatus((res as any).data);
    setLoading(false);
  }, [physicianId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!agreed || !blob) return;
    setSubmitting(true);
    const res = await apiClient.signPhysicianAgreement(physicianId, blob, TELEMED_AGREEMENT_VERSION);
    if (res.success) {
      toast.success('Agreement signed');
      setSigning(false);
      setAgreed(false);
      setBlob(null);
      await load();
      onSigned?.();
    } else {
      toast.error(res.error || 'Failed to sign agreement');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="text-sm text-gray-500">Loading agreement…</div>;
  // Standard clinics don't execute this contract; show nothing rather than an
  // empty state that implies something is missing.
  if (!status?.applicable) return null;

  const needsSignature = agreementNeedsSignature(status);
  const outdated = agreementIsOutdated(status);
  const adminRecorded = status.agreementAccepted && !status.hasSignature;

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="p-5">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-gray-900">{TELEMED_AGREEMENT_TITLE}</h3>

            {adminRecorded ? (
              <div className="mt-2 flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-amber-800">
                  Recorded by an administrator on {formatDate(status.agreementAcceptedAt)} — no signature
                  on file. Please sign it so we hold a complete record.
                </p>
              </div>
            ) : status.agreementAccepted ? (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span>
                  Signed {formatDate(status.agreementAcceptedAt)}
                  {status.agreementVersion && (
                    <span className="text-gray-400"> · version {status.agreementVersion}</span>
                  )}
                </span>
              </div>
            ) : (
              <div className="mt-2 flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-amber-800">Not yet signed.</p>
              </div>
            )}

            {outdated && (
              <p className="mt-2 text-sm text-amber-800">
                A newer version of this agreement is available ({TELEMED_AGREEMENT_VERSION}). Signing
                again is optional — nothing is blocked in the meantime.
              </p>
            )}

            <div className="mt-3 flex items-center gap-4">
              <button
                onClick={() => setShowText(v => !v)}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                {showText ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {showText ? 'Hide agreement' : 'Read agreement'}
              </button>
              {!signing && (needsSignature || outdated) && (
                <button
                  onClick={() => { setSigning(true); setShowText(true); }}
                  className="text-sm font-medium text-emerald-700 hover:text-emerald-900"
                >
                  {outdated ? 'Review & sign latest version' : 'Sign now'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showText && (
        <div className="border-t border-gray-200 px-5 py-4 max-h-96 overflow-y-auto bg-gray-50">
          {TELEMED_AGREEMENT_SECTIONS.map((sec, i) => (
            <div key={i} className="mb-4 last:mb-0">
              {sec.heading && <h4 className="font-medium text-gray-900 mb-1">{sec.heading}</h4>}
              {(sec.paragraphs || []).map((para: string, j: number) => (
                <p key={j} className="text-sm text-gray-700 mb-2 last:mb-0">{para}</p>
              ))}
            </div>
          ))}
          <p className="text-xs text-gray-400 mt-4">Version {TELEMED_AGREEMENT_VERSION}</p>
        </div>
      )}

      {signing && (
        <div className="border-t border-gray-200 p-5 space-y-4">
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              I have read and agree to the {TELEMED_AGREEMENT_TITLE}, and I am authorized to execute it
              on behalf of this clinic.
            </span>
          </label>

          <SignaturePad onChange={setBlob} disabled={submitting} />

          <div className="flex items-center gap-3">
            <button
              onClick={submit}
              disabled={!agreed || !blob || submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-300 disabled:text-gray-500"
            >
              {submitting ? 'Submitting…' : 'Submit signature'}
            </button>
            <button
              onClick={() => { setSigning(false); setAgreed(false); setBlob(null); }}
              disabled={submitting}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgreementCard;
